import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {CenteredPopover} from '../../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {rotate360Animation} from '../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useTimeout} from '../../../../srcCommon/helpers/hookHelpers';

/*
 * Constants.
 */

/** Maximum time in milliseconds to wait for demo inbox to load. */
const loadingTimeout = 7000;
/** Duration in milliseconds for which to show demo inbox loaded success state. */
const displaySuccessDuration = 2000;

/*
 * Style.
 */

const StyledModalDiv = styled.div`
  width: 265px;
  height: 64px;
  ${(p) => boxShadow(p.theme, `0px 4px 32px rgba(0, 0, 0, 0.15)`)};
  border-radius: 20px;
  background-color: ${(p) => p.theme.greys.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 22px;
`;

const StyledSpinnerDiv = styled.div`
  ${rotate360Animation(2)};
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade90};
`;

/*
 * Props.
 */

interface DemoInboxLoadingModalProps {
  /** Whether this modal is open. */
  isDemoInboxLoadingModalOpen: boolean;
  /** Whether the demo inbox has loaded. If so, we will briefly show a success state before closing. */
  hasDemoInboxLoaded: boolean;
  /** Handler to invoke after timing out (either bc the demo inbox took too long to load, or we are done loading). */
  onLoadingComplete: Handler;
}

/*
 * Component.
 */

export const DemoInboxLoadingModal: FC<DemoInboxLoadingModalProps> = ({
  isDemoInboxLoadingModalOpen,
  hasDemoInboxLoaded,
  onLoadingComplete,
}) => {
  // We should show the success state if the demo inbox has successfully loaded.
  const [shouldDisplaySuccess, setShouldDisplaySuccess] = useState(hasDemoInboxLoaded);
  // Keep track of whether the demo inbox was previously loaded, so we can show success for a few seconds.
  const previousHasDemoInboxLoadedRef = useRef(hasDemoInboxLoaded);

  const [safeSetTimeout] = useTimeout();

  useEffect(() => {
    // We're only interested in transition from false -> true.
    if (!isDemoInboxLoadingModalOpen) {
      return;
    }

    // We've opened the modal for the first time, so set a timeout for the max loading duration.
    safeSetTimeout(onLoadingComplete, loadingTimeout);
  }, [isDemoInboxLoadingModalOpen, onLoadingComplete, safeSetTimeout]);

  useEffect(() => {
    // We're only interested in transitions from false -> true.
    if (!isDemoInboxLoadingModalOpen || previousHasDemoInboxLoadedRef.current) {
      return;
    }

    // If the demo inbox has just loaded, update the ref, display success, and set a timeout to exit.
    if (hasDemoInboxLoaded) {
      previousHasDemoInboxLoadedRef.current = hasDemoInboxLoaded;
      setShouldDisplaySuccess(true);
      safeSetTimeout(onLoadingComplete, displaySuccessDuration);
    }
  }, [isDemoInboxLoadingModalOpen, hasDemoInboxLoaded, onLoadingComplete, safeSetTimeout]);

  if (!isDemoInboxLoadingModalOpen) {
    return null;
  }

  return (
    <CenteredPopover isExclusive>
      <StyledModalDiv>
        {renderIcon(shouldDisplaySuccess)}
        <StyledTitleDiv>
          <Trans id="demoinboxloadingmodal-title">Setting up sample inboxes...</Trans>
        </StyledTitleDiv>
      </StyledModalDiv>
    </CenteredPopover>
  );
};

/*
 * Helpers.
 */

function renderIcon(shouldDisplaySuccess: boolean) {
  if (!shouldDisplaySuccess) {
    return (
      <StyledSpinnerDiv>
        <Loader size={VisualSizesEnum.SMALL} variant={LoaderVariantsEnum.BASE} isEnabled />
      </StyledSpinnerDiv>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => <Icon name="checkmarkCircleSmall" color={theme.palette.green.shade40} />}
    </UserThemeConsumer>
  );
}
